import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { CheckCircle, AlertTriangle, XCircle, Lightbulb, BookOpen } from 'lucide-react'
import { useKnowledge } from './KnowledgeProvider'
import { hasKb } from '../data/knowledgeBase'

interface AnalysisCardProps {
  title: string
  checkKey: string
  /** Pořadí klíčů pro listování Předchozí/Další v popupu (volitelné). */
  navIds?: string[]
  result: {
    status: 'pass' | 'warning' | 'fail'
    message: string
    score: number
    [key: string]: unknown
  }
}

export default function AnalysisCard({ title, checkKey, navIds, result }: AnalysisCardProps) {
  const { t } = useTranslation()
  const { open: openKb } = useKnowledge()
  const kbAvailable = hasKb(checkKey)
  const desc = t(`check_details.${checkKey}_desc`, { defaultValue: '' })
  const recommendation = t(`check_details.${checkKey}_rec`, { defaultValue: '' })
  const why = t(`check_details.${checkKey}_why`, { defaultValue: '' })
  const example = t(`check_details.${checkKey}_example`, { defaultValue: '' })
  const icons = {
    pass: <CheckCircle className="w-5 h-5 text-green-500" />,
    warning: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
    fail: <XCircle className="w-5 h-5 text-red-500" />,
  }

  const bgColors = {
    pass: 'bg-green-50/60 border-green-200 dark:bg-emerald-500/[0.06] dark:border-emerald-500/25',
    warning: 'bg-yellow-50/60 border-yellow-200 dark:bg-amber-400/[0.07] dark:border-amber-400/25',
    fail: 'bg-red-50/60 border-red-200 dark:bg-red-500/[0.07] dark:border-red-500/25',
  }

  return (
    <div className={clsx('rounded-xl border p-5', bgColors[result.status])}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{icons[result.status]}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
            <span className={clsx(
              'text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ml-2',
              result.status === 'pass' && 'bg-green-100 text-green-700 dark:text-emerald-300',
              result.status === 'warning' && 'bg-yellow-100 text-yellow-700 dark:text-amber-300',
              result.status === 'fail' && 'bg-red-100 text-red-700 dark:text-red-300',
            )}>
              {result.score}b
            </span>
          </div>
          <p className="text-gray-700 text-sm mt-1">{result.message}</p>
          {desc && <p className="text-xs text-gray-500 mt-1.5">{desc}</p>}

          {/* Recommendation for non-passing checks — zvýrazněné, s důvodem a příkladem */}
          {result.status !== 'pass' && recommendation && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 dark:border-amber-400/20 dark:bg-amber-400/[0.06]">
              <div className="flex items-start gap-2.5">
                <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 leading-relaxed">
                    <span className="font-bold text-amber-700 dark:text-amber-300">{t('common.recommendation')}:</span> {recommendation}
                  </p>
                  {why && (
                    <p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed">
                      <span className="font-semibold text-gray-700">{t('common.why')}:</span> {why}
                    </p>
                  )}
                  {example && (
                    <div className="mt-2.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600/80 mb-1">{t('common.example')}</p>
                      <code className="block text-xs font-mono text-gray-800 bg-white border border-amber-100 rounded-lg px-3 py-2 whitespace-pre-wrap break-words leading-relaxed dark:border-amber-400/15 dark:text-gray-100">{example}</code>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Zjistit více — otevře popup s vysvětlením */}
          {kbAvailable && (
            <button
              onClick={() => openKb(checkKey, navIds)}
              className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 hover:gap-2 transition-all"
            >
              <BookOpen className="w-3.5 h-3.5" /> {t('kb.learn_more')}
            </button>
          )}

          {/* Extra details for specific checks */}
          {checkKey === 'title' && !!result.title && (
            <p className="text-xs text-gray-500 mt-2 font-mono bg-white/50 dark:bg-white/5 px-2 py-1 rounded truncate">
              {result.title as string} ({result.length as number} {t('card.chars')})
            </p>
          )}
          {checkKey === 'meta_description' && !!result.description && (
            <p className="text-xs text-gray-500 mt-2 bg-white/50 dark:bg-white/5 px-2 py-1 rounded line-clamp-2">
              {result.description as string} ({result.length as number} {t('card.chars')})
            </p>
          )}
          {checkKey === 'meta_keywords' && !!result.value && (
            <p className="text-xs text-gray-500 mt-2 bg-white/50 dark:bg-white/5 px-2 py-1 rounded line-clamp-2 font-mono">
              {result.value as string} ({result.count as number}x)
            </p>
          )}
          {checkKey === 'images' && result.total !== undefined && (
            <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
              <span>{t('card.total')} {result.total as number}</span>
              {(result.without_alt as number) > 0 && <span className="text-yellow-600">{t('card.without_alt')} {result.without_alt as number}</span>}
              {(result.old_formats as number) > 0 && <span className="text-yellow-600">{t('card.old_formats')} {result.old_formats as number}</span>}
              {(result.modern_formats as number) > 0 && <span className="text-green-600">WebP/AVIF: {result.modern_formats as number}</span>}
            </div>
          )}
          {checkKey === 'headings' && !!result.headings && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {Object.entries(result.headings as Record<string, string[]>).map(([tag, texts]) =>
                texts.length > 0 ? (
                  <span key={tag} className="text-xs bg-white/60 dark:bg-white/5 px-2 py-0.5 rounded text-gray-600">
                    {tag.toUpperCase()}: {texts.length}x
                  </span>
                ) : null
              )}
            </div>
          )}
          {checkKey === 'performance' && (
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
              {result.load_time_ms != null && <span>{t('card.load')} {result.load_time_ms as number}ms</span>}
              {result.page_size_kb != null && <span>{t('card.size')} {result.page_size_kb as number} KB</span>}
              {result.css_files != null && <span>CSS: {result.css_files as number}</span>}
              {result.js_files != null && <span>JS: {result.js_files as number}</span>}
            </div>
          )}
          {checkKey === 'links' && (
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
              {result.internal != null && <span>{t('card.internal')} {result.internal as number}</span>}
              {result.external != null && <span>{t('card.external')} {result.external as number}</span>}
            </div>
          )}
          {checkKey === 'content_quality' && (
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
              {result.word_count != null && <span>{t('card.words', { count: result.word_count as number })}</span>}
              {result.reading_time_min != null && <span>{t('card.reading_time', { count: result.reading_time_min as number })}</span>}
              {result.paragraphs != null && <span>{t('card.paragraphs', { count: result.paragraphs as number })}</span>}
            </div>
          )}
          {checkKey === 'social' && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {(result.networks as string[] | undefined)?.map((n) => (
                <span key={n} className="text-xs bg-white/60 dark:bg-white/5 px-2 py-0.5 rounded text-gray-600 dark:text-gray-300">{n}</span>
              ))}
              {result.og_complete ? (
                <span className="text-xs text-green-600 dark:text-green-400">Open Graph ✓</span>
              ) : (result.has_og_image as boolean) === false ? (
                <span className="text-xs text-yellow-600 dark:text-yellow-400">og:image ✗</span>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
